/**
 * CF Error Code → UI Message Mapping
 *
 * Canonical mapping for all Cloud Function errors from membership management callables.
 * Governed by: UI Team Authorization Contract V1.1 §7
 * (docs/ui/UI_TEAM_AUTHORIZATION_CONTRACT_V1.md)
 *
 * RULES:
 * - No raw error codes or technical messages may be shown to the user.
 * - All membership callable errors must be mapped through this function.
 * - 'unauthenticated' must trigger a redirect to login — callers are responsible for this.
 */

export const CF_ERROR_MESSAGES: Record<string, string> = {
  'failed-precondition': 'Er moet altijd minimaal één actieve eigenaar zijn.',
  'permission-denied': 'Je hebt geen rechten om deze actie uit te voeren.',
  'not-found': 'Dit teamlid bestaat niet meer. Ververs de pagina.',
  'resource-exhausted': 'Wacht even voordat je deze actie opnieuw uitvoert.',
  'invalid-argument': 'Er is een ongeldige waarde opgegeven. Probeer het opnieuw.',
  'already-exists': 'Dit e-mailadres heeft al een openstaande uitnodiging.',
  cancelled: 'Deze uitnodiging is ingetrokken.',
  'deadline-exceeded': 'De actie duurde te lang. Probeer het opnieuw.',
  internal: 'Er is een interne fout opgetreden. Probeer het opnieuw.',
};

const FALLBACK_MESSAGE = 'Er is een fout opgetreden. Probeer het opnieuw.';

/**
 * Maps a Firebase HttpsError code to a canonical NL user message.
 * Returns null for 'unauthenticated' — callers must redirect to login.
 */
export function mapCfError(error: unknown): string | null {
  if (!error || typeof error !== 'object') return FALLBACK_MESSAGE;

  const code = (error as { code?: string }).code ?? '';

  if (code === 'unauthenticated') {
    return null;
  }

  return CF_ERROR_MESSAGES[code] ?? FALLBACK_MESSAGE;
}

/**
 * Returns true if the error code is 'unauthenticated'.
 * Callers must redirect to login when this returns true.
 */
export function isCfUnauthenticated(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  return (error as { code?: string }).code === 'unauthenticated';
}
